import React, { useState, useEffect } from 'react';
import './FacilityFilters.css';
import { groupFacilitiesByCategory } from '../../hooks/facilityConfig';

const FacilityFilters = ({ onFiltersChange, filteredCount, totalCount }) => {
  // Filter categories with labels
  const CATEGORIES = {
    work: { label: 'Work', icon: '💼' },
    recreation: { label: 'Recreation', icon: '🏃' },
    dining: { label: 'Dining', icon: '🍽️' },
    event: { label: 'Event', icon: '🎉' },
    accommodation: { label: 'Accommodation', icon: '🛏️' }
  };

  // Time ranges for filtering
  const TIME_RANGES = {
    morning: { label: 'Morning', range: '05:00-12:00', icon: '🌅' },
    afternoon: { label: 'Afternoon', range: '12:00-17:00', icon: '☀️' },
    evening: { label: 'Evening', range: '17:00-23:00', icon: '🌙' }
  };

  // State for filters
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTimeRanges, setSelectedTimeRanges] = useState(new Set());
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  // Update parent component when filters change
  useEffect(() => {
    const filters = {
      categories: Array.from(selectedCategories),
      searchTerm: searchTerm.trim(),
      timeRanges: Array.from(selectedTimeRanges),
      showAvailableOnly
    };
    
    // Check if any filters are active
    const activeFilters = 
      filters.categories.length > 0 ||
      filters.searchTerm.length > 0 ||
      filters.timeRanges.length > 0 ||
      filters.showAvailableOnly;
    
    setHasActiveFilters(activeFilters);
    onFiltersChange(filters);
  }, [selectedCategories, searchTerm, selectedTimeRanges, showAvailableOnly, onFiltersChange]);

  // Toggle category selection
  const toggleCategory = (category) => {
    const newCategories = new Set(selectedCategories);
    if (newCategories.has(category)) {
      newCategories.delete(category);
    } else {
      newCategories.add(category);
    }
    setSelectedCategories(newCategories);
  };

  // Toggle time range selection
  const toggleTimeRange = (timeRange) => {
    const newTimeRanges = new Set(selectedTimeRanges);
    if (newTimeRanges.has(timeRange)) {
      newTimeRanges.delete(timeRange);
    } else {
      newTimeRanges.add(timeRange);
    }
    setSelectedTimeRanges(newTimeRanges);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedCategories(new Set());
    setSearchTerm('');
    setSelectedTimeRanges(new Set());
    setShowAvailableOnly(false);
  };

  // Get count of facilities by category
  const getCategoryCount = (category) => {
    const grouped = groupFacilitiesByCategory();
    return grouped[category]?.length || 0;
  };

  return (
    <div className="facility-filters">
      {/* Mobile Filter Toggle */}
      <button 
        className="filter-toggle-mobile"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className="filter-icon">🔍</span>
        <span>Filters</span>
        {hasActiveFilters && <span className="active-indicator"></span>}
        <span className="toggle-arrow">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {/* Filter Panel */}
      <div className={`filter-panel ${isExpanded ? 'expanded' : ''}`}>
        {/* Search Input */}
        <div className="filter-section search-section">
          <h3 className="filter-title">Search Facilities</h3>
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search facilities"
            />
            {searchTerm && (
              <button 
                className="clear-search"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Category Filters */}
        <div className="filter-section category-section">
          <h3 className="filter-title">Categories</h3>
          <div className="filter-buttons">
            {Object.entries(CATEGORIES).map(([key, category]) => (
              <button
                key={key}
                className={`filter-button category-button ${
                  selectedCategories.has(key) ? 'active' : ''
                }`}
                onClick={() => toggleCategory(key)}
                aria-pressed={selectedCategories.has(key)}
              >
                <span className="button-icon">{category.icon}</span>
                <span className="button-label">{category.label}</span>
                <span className="button-count">({getCategoryCount(key)})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Time Range Filters */}
        <div className="filter-section time-section">
          <h3 className="filter-title">Time Range</h3>
          <div className="filter-buttons">
            {Object.entries(TIME_RANGES).map(([key, timeRange]) => (
              <button
                key={key}
                className={`filter-button time-button ${
                  selectedTimeRanges.has(key) ? 'active' : ''
                }`}
                onClick={() => toggleTimeRange(key)}
                aria-pressed={selectedTimeRanges.has(key)}
              >
                <span className="button-icon">{timeRange.icon}</span>
                <span className="button-label">{timeRange.label}</span>
                <span className="button-time">{timeRange.range}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Additional Options */}
        <div className="filter-section options-section">
          <label className="filter-option">
            <input
              type="checkbox"
              checked={showAvailableOnly}
              onChange={(e) => setShowAvailableOnly(e.target.checked)}
            />
            <span className="option-label">Show available only</span>
          </label>
        </div>

        {/* Filter Actions */}
        <div className="filter-actions">
          <button 
            className="clear-filters-button"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            Clear all filters
          </button>
          <div className="filter-summary">
            {filteredCount !== undefined && totalCount !== undefined && (
              <span className="result-count">
                Showing {filteredCount} of {totalCount} facilities
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityFilters;